// Email-domain gating for the "Modul Pengurusan Pengguna" menu group.
//
// The group is only shown to internal Unijaya staff, identified by an email
// whose LOCAL PART ends in ".unijaya" — e.g. hafiz.unijaya@gmail.com or
// sol.unijaya@yahoo.com. The mailbox provider is deliberately ignored: staff
// use assorted free providers, so the marker lives in the local part, not the
// domain.
//
// NOTE: this is a MENU-VISIBILITY filter only. The /penggunasistem and
// /tetapkanpenyeliaprosesanoperasi routes remain reachable by direct URL, and
// their APIs are unchanged. Do not treat this as an access control boundary.

import axios from '@/plugins/axios';

const UNIJAYA_LOCALPART = /\.unijaya$/i;

// True when the local part ends in ".unijaya". Splits on the LAST '@' so a
// quoted local part containing '@' can't smuggle the marker in from the domain.
export function isUnijayaEmail(email) {
  if (typeof email !== 'string') return false;
  const at = email.trim().lastIndexOf('@');
  if (at <= 0) return false;
  return UNIJAYA_LOCALPART.test(email.trim().slice(0, at));
}

function cachedUser() {
  try {
    return JSON.parse(window.localStorage.getItem('user')) || {};
  } catch (e) {
    return {};
  }
}

/**
 * The logged-in user's email, read fresh from the server on every call.
 *
 * Deliberately NOT cache-first. The email can change after login — the user can
 * edit their own via the profile dialog, and an ADMIN can change someone else's
 * from Pengguna — while tokens are non-expiring by default, so a cached copy can
 * stay wrong indefinitely. Trusting localStorage would leave the gated menu
 * visible to a user who no longer qualifies (and hidden from one who now does).
 * The server is the only thing that knows the current address, so we ask it.
 *
 * Uses /penggunasistem/paparmaklumatpenggunaid (resolves the caller from
 * req.payload.uid). It MUST NOT live under /auth: the axios interceptor strips
 * the Authorization header from any URL containing '/auth' (see plugins/axios.js),
 * so a jwtVerify-guarded /auth route 403s on every call.
 *
 * The response is written back to localStorage['user'] so the cached copy tracks
 * the server rather than drifting from it.
 *
 * Never throws. If the request fails we fall back to the cached email — the menu
 * is cosmetic (the routes are unguarded either way), so a network blip degrading
 * to a slightly stale menu beats yanking it from staff mid-session — but we warn,
 * because a silent fallback here looks exactly like the feature not working.
 */
export async function getUserEmail() {
  const user = cachedUser();
  if (!user.id) return null;

  try {
    const res = await axios.get(`${process.env.VUE_APP_API_URL}/penggunasistem/paparmaklumatpenggunaid`);
    const email = (res.data && res.data.emel) || null;
    window.localStorage.setItem('user', JSON.stringify({ ...user, email }));
    return email;
  } catch (e) {
    console.warn('emailScope: gagal membaca emel semasa, guna cache', e);
    return user.email || null;
  }
}
